<template>
  <div class="app-container">
    <el-form ref="form" :model="form" label-width="120px">
      <el-form-item label="文件名称">
        <el-input v-model="form.name" class="input" placeholder="请输入需要查询的文件名称" />
        <el-button class="query-btn" @click="fetchData">查询</el-button>
        <el-button type="primary" @click="showUploadDialog">上传</el-button>
      </el-form-item>
    </el-form>
    <el-table
      ref="table"
      v-loading="listLoading"
      :data="list"
      element-loading-text="Loading"
      height="calc(100vh - 200px)"
      border=""
      :header-cell-style="{background:'rgb(244 244 244)'}"
      :empty-text="'暂无数据'"
    >
      <el-table-column align="center" label="文件名称" min-width="180" fixed="left">
        <template slot-scope="scope">
          {{ scope.row.name }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="创建时间" min-width="180">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="拆分状态" min-width="180">
        <template slot-scope="scope">
          {{ scope.row.sliceStatus ? '已拆分' : '未拆分' }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="拆分数量" min-width="180">
        <template slot-scope="scope">
          {{ scope.row.sliceCount }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" min-width="280" fixed="right">
        <template slot-scope="scope">
          <el-button @click="viewFile(scope.row)">查看</el-button>
          <el-button @click="openSliceDialog(scope.row)">切片</el-button>
          <el-button @click="delItem(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-dialog title="上传文件" :visible.sync="uploadDialogVisible" width="800px">
      <div class="upload-dialog-content">
        <div class="upload-section">
          <span class="section-label">文件上传</span>
          <el-upload
            action="#"
            :auto-upload="false"
            :on-remove="handleRemove"
            :on-change="handleChange"
            :file-list="fileList"
            accept="file"
          >
            <el-button size="small" type="primary">
              点击上传(支持markdown,条款pdf和excel三种格式)
            </el-button>
          </el-upload>
        </div>
        <div class="content-section">
          <span class="section-label">知识内容</span>
          <div class="content-list">
            <template v-if="uploadContent[0]">
              <div v-for="(text, index) in uploadContent" :key="index" class="content-item">
                <span class="item-index">{{ index + 1 }}</span>
                <div class="item-content">
                  <el-input v-loading="uploadLoading" readonly type="textarea" :value="text" :rows="3" />
                </div>
              </div>
            </template>
            <template v-else>
              <el-input v-loading="uploadLoading" readonly type="textarea" placeholder="请选择文件进行转换" :rows="5" />
            </template>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="uploadDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!fileList.length" @click="confirmUpload">确定</el-button>
      </span>
    </el-dialog>

    <!-- 查看文件弹窗 -->
    <el-dialog :title="viewFileTitle" :visible.sync="viewDialogVisible" width="800px" top="5%">
      <div class="view-file-content" v-loading="viewLoading">
        <!-- PDF 文件 -->
        <div v-if="viewFileType === 'pdf'" class="pdf-viewer">
          <div class="pdf-content">
            <pdf 
              :src="pdfFile" 
              :page="pdfCurrentPage"
              @num-pages="pdfTotalPages = $event"
              @page-loaded="pdfCurrentPage = $event"
              @error="handlePdfError"
            />
          </div>
          <div class="pdf-controls">
            <el-button @click="prevPage" :disabled="pdfCurrentPage <= 1">上一页</el-button>
            <span>{{ pdfCurrentPage }} / {{ pdfTotalPages }}</span>
            <el-button @click="nextPage" :disabled="pdfCurrentPage >= pdfTotalPages">下一页</el-button>
          </div>
        </div>
        <!-- Markdown 文件 -->
        <div v-else-if="viewFileType === 'md'" class="md-viewer">
          <pre>{{ viewFileContent }}</pre>
        </div>
        <!-- Excel 文件 -->
        <div v-else-if="viewFileType === 'excel'" class="excel-viewer">
          <vue-office-excel :src="viewFileUrl" @rendered="handleExcelRendered" @error="handleExcelError" />
        </div>
        <!-- 其他文件类型 -->
        <div v-else class="file-viewer">
          <p>不支持预览此文件类型，请<a :href="viewFileUrl" target="_blank">点击下载</a>查看</p>
          <a :href="viewFileUrl" target="_blank" class="download-link">
            <el-button type="primary">下载文件</el-button>
          </a>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 切片弹窗 -->
    <el-dialog :title="sliceTitle" :visible.sync="sliceDialogVisible" width="800px" top="5%">
      <div class="slice-dialog-content" v-loading="sliceLoading">
        <div v-if="sliceList.length === 0" class="empty-tip">暂无切片内容</div>
        <div v-else class="slice-list">
          <div v-for="(item, index) in sliceList" :key="item.id" class="slice-item">
            <span class="slice-index">{{ index + 1 }}</span>
            <el-input
              v-model="item.content"
              type="textarea"
              :rows="3"
              placeholder="请输入切片内容"
              class="slice-input"
            />
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="sliceDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="saveSlice">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fileSplit, getUploadUrl, fileAdd, queryFileList, knowdocDelete, getFileUrl, knowdocSliceList, knowdocSliceBatchUpd } from '@/api/knowledgeUpload'
import VueOfficeExcel from '@vue-office/excel'
import pdf from 'vue-pdf'
export default {
  components: {
    VueOfficeExcel,
    pdf
  },
  data() {
    return {
      courseId: null,
      form: {
        name: ''
      },
      list: [],
      listLoading: false,
      uploadDialogVisible: false,
      uploadLoading: false,
      fileList: [],
      uploadContent: [''],
      // 查看文件相关
      viewDialogVisible: false,
      viewLoading: false,
      viewFileUrl: '',
      viewFileContent: '',
      viewFileType: '',
      viewFileTitle: '',
      // PDF相关
      pdfCurrentPage: 1,
      pdfTotalPages: 1,
      pdfFile: null,
      // 切片相关
      sliceDialogVisible: false,
      sliceLoading: false,
      sliceList: [],
      sliceListOriginal: [],
      sliceTitle: ''
    }
  },
  created() {
    this.courseId = this.$route.query.courseId
    if (this.courseId) {
      this.fetchData()
    }
  },
  methods: {
    async fetchData() {
      this.listLoading = true
      const params = {
        page: 1,
        pageSize: 99,
        courseId: this.courseId,
        name: this.form.name
      }
      try {
        const response = await queryFileList(params)
        this.list = response.data.records || []
      } catch (error) {
        this.$message({
          message: error.message || '查询失败',
          type: 'error',
          duration: 2 * 1000
        })
      } finally {
        this.listLoading = false
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    viewFile(row) {
      if (this.viewLoading) {
        return
      }
      if (!row.url) {
        this.$message({
          message: '文件URL不存在',
          type: 'error',
          duration: 2 * 1000
        })
        return
      }
      this.viewLoading = true
      this.viewFileTitle = row.name
      // 获取文件类型
      const fileName = row.name || ''
      const ext = fileName.split('.').pop().toLowerCase()
      this.viewFileType = this.getFileType(ext)
      // 重置PDF页码
      this.pdfCurrentPage = 1
      this.pdfTotalPages = 1
      // 调用接口获取文件URL
      getFileUrl({ fileKey: row.url }).then(res => {
        this.viewFileUrl = res.data.url
        // 如果是PDF文件，加载PDF
        if (this.viewFileType === 'pdf') {
          this.pdfFile = pdf.createLoadingTask(res.data.url)
        }
        // 如果是markdown文件，尝试获取内容
        if (this.viewFileType === 'md') {
          this.fetchFileContent(res.data.url)
        }
        this.viewDialogVisible = true
      }).catch(() => {
        this.$message({
          message: '获取文件URL失败',
          type: 'error',
          duration: 2 * 1000
        })
      }).finally(() => {
        this.viewLoading = false
      })
    },
    prevPage() {
      if (this.pdfCurrentPage > 1) {
        this.pdfCurrentPage--
      }
    },
    nextPage() {
      if (this.pdfCurrentPage < this.pdfTotalPages) {
        this.pdfCurrentPage++
      }
    },
    handlePdfError(error) {
      console.error('PDF加载错误:', error)
      this.$message({
        message: 'PDF加载失败，请尝试下载查看',
        type: 'error',
        duration: 2 * 1000
      })
    },
    getFileType(ext) {
      const pdfExts = ['pdf']
      const mdExts = ['md', 'markdown']
      const excelExts = ['xls', 'xlsx', 'xlsm', 'xlsb', 'xltx', 'xltm']
      if (pdfExts.includes(ext)) return 'pdf'
      if (mdExts.includes(ext)) return 'md'
      if (excelExts.includes(ext)) return 'excel'
      return 'other'
    },
    async fetchFileContent(url) {
      try {
        const response = await fetch(url)
        this.viewFileContent = await response.text()
      } catch (error) {
        console.error('获取文件内容失败:', error)
        this.viewFileContent = '无法获取文件内容，请下载查看'
      }
    },
    handleExcelRendered() {
      console.log('Excel 渲染完成')
    },
    handleExcelError() {
      this.$message({
        message: 'Excel 文件解析失败，请下载查看',
        type: 'warning',
        duration: 2 * 1000
      })
    },
    async openSliceDialog(row) {
      if (this.sliceLoading) return
      this.sliceTitle = row.name + ' - 切片'
      this.sliceLoading = true
      try {
        const response = await knowdocSliceList({ docId: row.id })
        this.sliceList = response.data || []
        this.sliceList.forEach(item => {
          this.$set(item, 'content', item.content || '')
        })
        this.sliceListOriginal = JSON.parse(JSON.stringify(this.sliceList))
        this.sliceDialogVisible = true
      } catch (error) {
        this.$message({
          message: '获取切片列表失败',
          type: 'error',
          duration: 2 * 1000
        })
      } finally {
        this.sliceLoading = false
      }
    },
    async saveSlice() {
      if (!this.sliceList.length) return
      const changedItems = this.sliceList.filter((item, index) => {
        const original = this.sliceListOriginal[index]
        return original && original.content !== item.content
      })
      if (changedItems.length === 0) {
        this.sliceDialogVisible = false
        return
      }
      try {
        await knowdocSliceBatchUpd(changedItems.map(item => ({
          id: item.id,
          content: item.content
        })))
        this.$message({
          message: '保存成功',
          type: 'success',
          duration: 2 * 1000
        })
        this.sliceDialogVisible = false
      } catch (error) {
        this.$message({
          message: '保存失败',
          type: 'error',
          duration: 2 * 1000
        })
      }
    },
    showUploadDialog() {
      this.fileList = []
      this.uploadContent = ['']
      this.uploadDialogVisible = true
    },
    handleRemove() {
      this.fileList = []
      this.uploadContent = ['']
    },
    handleChange(file, fileList) {
      this.uploadLoading = true
      this.fileList = [file]
      const formData = new FormData()
      formData.append('file', file.raw)
      fileSplit(formData).then(res => {
        this.uploadContent = res.data
      }).catch(() => {
        this.fileList = []
        this.uploadContent = ['']
      }).finally(() => {
        this.uploadLoading = false
      })
    },
    async confirmUpload() {
      if (!this.fileList.length) {
        this.$message({
          message: '请上传文件',
          type: 'error',
          duration: 2 * 1000
        })
        return
      }
      try {
        const file = this.fileList[0].raw
        const fileName = file.name
        const uploadUrlRes = await getUploadUrl({ fileName })
        const { uploadUrl, fileKey } = uploadUrlRes.data
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': 'application/octet-stream'
          }
        })
        if (!uploadResponse.ok) {
          throw new Error(`文件上传失败，HTTP状态码: ${uploadResponse.status}`)
        }
        const fileParams = {
          courseId: this.courseId,
          // name: fileName,
          fileUrl: fileKey,
          sliceContents: this.uploadContent
        }
        await fileAdd(fileParams)
        this.$message({
          message: '上传成功',
          type: 'success',
          duration: 2 * 1000
        })
        this.uploadDialogVisible = false
        this.fetchData()
      } catch (error) {
        this.$message({
          message: error.message || '上传失败',
          type: 'error',
          duration: 2 * 1000
        })
      }
    },
    delItem(id) {
      this.$confirm('此操作将删除该文件, 是否继续?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        knowdocDelete({ id }).then(() => {
          this.$message({
            type: 'success',
            message: '删除成功'
          })
          this.fetchData()
        }).catch(() => {
          this.$message({
            type: 'error',
            message: '删除失败'
          })
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep{
  .el-form{
    display: flex;
    justify-content: space-between;
  }
  .el-table{
    overflow: auto;
  }
  .el-form-item__content{
    display: flex;
    position: relative;
  }
  .el-button {
    margin-left: 0;
    padding: 10px 18px;
    margin-right: 20px;
    &:focus, &:hover{
      background: #FFF;
      border: 1px solid #DCDFE6;
      color: #606266;
    }
  }
  .el-button:last-child{
    margin-right: 0;
    color: #FFF;
    background-color: #409EFF;
  }
  .input{
    .el-input__inner {
      width: 300px;
    }
  }
}
.query-btn {
  margin-left: 20px;
}
.v-md-editor-preview{
  overflow: auto;
  border-radius: 4px;
  border: 1px solid #DCDFE6;
  padding: 10px 20px;
  width: 100%;
  margin-bottom: 10px;
  min-height: 54px;
}
.upload-dialog-content {
  padding: 20px;
}
.upload-section {
  margin-bottom: 20px;
}
.section-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: #606266;
}
.content-section {
  max-height: 400px;
  overflow: auto;
}
.content-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.content-item {
  display: flex;
  gap: 10px;
}
/* 查看文件弹窗样式 */
.view-file-content {
  max-height: 500px;
  overflow-x: hidden;
  padding: 10px;
}
.md-viewer {
  max-height: 480px;
  overflow-y: auto;
}
.md-viewer pre {
  margin: 0;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
}
.pdf-viewer {
  display: flex;
  flex-direction: column;
  max-height: 480px;
}
.pdf-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  background-color: #f5f5f5;
}
.pdf-content :deep(.vue-pdf) {
  width: 100%;
  max-width: 600px;
}
.pdf-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 10px;
  background-color: #fff;
  border-top: 1px solid #e4e7ed;
}
.pdf-controls span {
  font-size: 14px;
  color: #606266;
}
.excel-viewer {
  height: 480px;
}
.excel-viewer :deep(.vue-office-excel) {
  height: 100%;
}
.file-viewer {
  padding: 20px;
  text-align: center;
}
.download-link {
  display: inline-block;
  margin-top: 15px;
}
.slice-dialog-content {
  max-height: 500px;
  overflow-y: auto;
  padding: 10px;
}
.slice-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.slice-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.slice-index {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  background: #409EFF;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.slice-input {
  flex: 1;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 40px 0;
}
.item-index {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  background: #409EFF;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.item-content {
  flex: 1;
}
</style>

